import type { MessageView, ReviewThreadEntry, ThreadMessagesPage } from '@discovr/types';

/**
 * The single MessageView → ReviewThreadEntry projection.
 *
 * This used to be copy-pasted into four surfaces (ScriptsTab, ContentTab's
 * types.ts, ReviewThreadDrawer and ConfirmedTab) which then silently drifted —
 * ConfirmedTab stopped carrying `sender_id`/`edited`, so the "edited" marker
 * never rendered on the Finalized view even though it was the same thread.
 * Every new MessageView field has to land here once, not four times.
 *
 * `actorFor` exists because ReviewThreadDrawer maps the raw BE role through
 * `senderRoleToActor` while the tab surfaces pass it straight through — the one
 * genuine difference between the old copies. Omitted: the role is used as-is.
 */
export function mapThreadMessages(
  page: ThreadMessagesPage | undefined,
  actorFor: (senderRole: string) => string = (senderRole) => senderRole,
): ReviewThreadEntry[] {
  if (!page) return [];
  return page.messages.map((m: MessageView) => ({
    id: m.id,
    actor: actorFor(m.sender_role),
    sender_id: m.sender_id || undefined,
    body: m.body,
    created_at: m.created_at,
    sender_name: m.sender_name || undefined,
    sender_title: m.sender_title || undefined,
    edited: m.edited,
    // kind lets ChatThread render decision-event rows (kind="rejection")
    // differently from chat bubbles.
    kind: m.kind,
    anchor: m.anchor,
    reply_to: m.reply_to,
    sender_avatar: m.sender_avatar,
    viewer_can_edit: m.viewer_can_edit,
    viewer_can_delete: m.viewer_can_delete,
    attachments: m.attachments,
  }));
}
